import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { ExpandableSection, SpaceBetween, Alert, Button, ButtonDropdown, Spinner, Grid, Box, Icon } from '@amzn/awsui-components-core';
import { EmployeeSearchBar } from '../components/attendees/EmployeeSearchBar';
import { AttendeeCard } from '../components/attendees/AttendeeCard';
import {
  MeetingFormData,
  AttendeePriority,
  AttendeeListProps,
  DocumentUploadProps,
  SharedMeetingViewProps,
  RecentMeeting,
  ResolvedMatch
} from '../interfaces/interface';
import { UploadTemplateContainer } from './modals/UploadTemplateModal';
import { ImportFromMeetingModal } from './modals/ImportFromMeetingModal';
import { CopyDropdown } from '../components/copy/CopyDropdown';
import { loadAttendeesInBatches } from '../utils/attendeeUtils';
import { logger } from 'src/logger';
import './AddAttendeesContainer.scss';

interface AddAttendeesContainerProps {
  formData: MeetingFormData;
  onInputChange: (field: keyof MeetingFormData, value: string) => void;
  attendees: AttendeeListProps;
  document: DocumentUploadProps;
  sharedMeeting: SharedMeetingViewProps;
  executiveAssistants: Array<{ name: string; email: string }>;
  onMultiBadgerClick: () => void;
  onDownloadCsv: () => void;
  initialExpanded?: boolean;
  recentMeetings: RecentMeeting[];
  initialShowCopyFromMeeting?: boolean;
  initialShowUpload?: boolean;
}

export const AddAttendeesContainer: React.FC<AddAttendeesContainerProps> = ({
  formData,
  onInputChange,
  attendees,
  document: doc,
  sharedMeeting,
  executiveAssistants,
  onMultiBadgerClick,
  onDownloadCsv,
  initialExpanded = true,
  recentMeetings,
  initialShowCopyFromMeeting = false,
  initialShowUpload = false
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [showUploadLocal, setShowUploadLocal] = useState(initialShowUpload);
  const [showCopyFromMeeting, setShowCopyFromMeeting] = useState(initialShowCopyFromMeeting);

  const {
    resolvedAttendees,
    existingEmails,
    duplicateEmails,
    onAddAttendee,
    onAttendeeUpdate,
    onAttendeeRemove,
    onToggleFavorite,
    onAddProxy,
    onResort
  } = attendees;
  const { isProcessingDocument, documentError, onFileSelected, onFormattedContentExtracted, showUploadModal, onHideUploadModal } = doc;
  const { isLoadingSharedMeeting, sharedMeetingProgress, isReadOnly, onSaveMeeting, isSaving } = sharedMeeting;

  const principalsWithProxy = useMemo(
    () => new Set(resolvedAttendees.filter((a) => a.proxyForLogin).map((a) => a.proxyForLogin!.toLowerCase())),
    [resolvedAttendees]
  );

  const handleAttendeeUpdateWithResort = useCallback(
    (id: string, updates: Partial<ResolvedMatch>) => {
      onAttendeeUpdate(id, updates);
      if ('priority' in updates) onResort();
    },
    [onAttendeeUpdate, onResort]
  );

  // Close upload modal when processing completes successfully
  const wasProcessing = useRef(false);
  useEffect(() => {
    if (isProcessingDocument) {
      wasProcessing.current = true;
    } else if (wasProcessing.current) {
      wasProcessing.current = false;
      if (!documentError) setShowUploadLocal(false);
    }
  }, [isProcessingDocument, documentError]);

  const criticalAttendees = resolvedAttendees.filter((a) => a.priority === AttendeePriority.Critical);
  const requiredAttendees = resolvedAttendees.filter((a) => a.priority === AttendeePriority.Required);
  const optionalAttendees = resolvedAttendees.filter((a) => a.priority === AttendeePriority.Optional);

  return (
    <>
      <ExpandableSection
        variant="container"
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
        headerText="Attendee List"
        headerActions={
          <SpaceBetween direction="horizontal" size="xs">
            {!isReadOnly && (
              <span className="blue-button">
                <ButtonDropdown
                  variant="primary"
                  disabled={isLoadingSharedMeeting}
                  items={[
                    { id: 'upload-template', text: 'Upload from Template', iconName: 'upload' },
                    { id: 'copy-from-meeting', text: 'Import from Meeting', iconName: 'copy' }
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === 'upload-template') setShowUploadLocal(true);
                    else if (detail.id === 'copy-from-meeting') setShowCopyFromMeeting(true);
                  }}
                >
                  Load Attendees
                </ButtonDropdown>
              </span>
            )}
            {onSaveMeeting && (
              <span className="blue-button">
                <Button variant="primary" onClick={onSaveMeeting} loading={isSaving} disabled={isSaving || resolvedAttendees.length === 0}>
                  Save
                </Button>
              </span>
            )}
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="vertical" size="xs">
          {!isReadOnly && (
            <div className="search-toolbar-row">
              <div className="search-toolbar-left">
                <div className="search-bar-wrapper">
                  <EmployeeSearchBar onAddAttendee={onAddAttendee} existingEmails={existingEmails} />
                </div>
                <CopyDropdown
                  resolvedAttendees={resolvedAttendees}
                  executiveAssistants={executiveAssistants}
                  onMultiBadgerClick={onMultiBadgerClick}
                  onDownloadCsv={onDownloadCsv}
                />
              </div>
            </div>
          )}

          {resolvedAttendees.length > 0 && (
            <SpaceBetween direction="vertical" size="xs">
              <Box padding={{ vertical: 'xxs' }}>
                <div className="attendees-header-wrapper">
                  <Grid gridDefinition={[{ colspan: 6 }, { colspan: 5 }, { colspan: 1 }]}>
                    <Box>
                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                        <Box fontSize="heading-m" fontWeight="bold" textAlign="left">
                          Attendees ({resolvedAttendees.length})
                          {resolvedAttendees.length > 0 && (
                            <Box display="inline" fontSize="body-s" color="text-body-secondary" fontWeight="normal" margin={{ left: 'xs' }}>
                              {' '}
                              ({criticalAttendees.length > 0 && `${criticalAttendees.length} critical`}
                              {criticalAttendees.length > 0 && (requiredAttendees.length > 0 || optionalAttendees.length > 0) && ', '}
                              {requiredAttendees.length > 0 && `${requiredAttendees.length} required`}
                              {requiredAttendees.length > 0 && optionalAttendees.length > 0 && ', '}
                              {optionalAttendees.length > 0 && `${optionalAttendees.length} optional`})
                            </Box>
                          )}
                        </Box>
                      </SpaceBetween>
                    </Box>
                    <div className="attendee-column attendee-column--ea">
                      <Box fontSize="heading-m" fontWeight="bold">
                        Executive Assistants ({executiveAssistants.length})
                      </Box>
                    </div>
                    <div />
                  </Grid>
                </div>
              </Box>

              {isProcessingDocument || isLoadingSharedMeeting ? (
                <Alert statusIconAriaLabel="Loading" type="info" header={isLoadingSharedMeeting ? 'Loading Shared Meeting' : 'Document Processing'}>
                  <SpaceBetween direction="horizontal" size="s" alignItems="center">
                    <Spinner size="normal" />
                    <span>
                      {isLoadingSharedMeeting
                        ? sharedMeetingProgress || 'Loading shared meeting attendees...'
                        : 'Processing document and resolving attendees...'}
                    </span>
                  </SpaceBetween>
                </Alert>
              ) : (
                <>
                  {duplicateEmails.size > 0 && (
                    <Alert statusIconAriaLabel="Warning" type="warning" header="Duplicate Email Alert">
                      Some attendees have duplicate email addresses. They will only be invited once.
                    </Alert>
                  )}
                  <div className="attendees-list">
                    {resolvedAttendees.map((attendee) => (
                      <AttendeeCard
                        key={attendee.id}
                        attendee={attendee}
                        onUpdate={handleAttendeeUpdateWithResort}
                        onRemove={onAttendeeRemove}
                        isDuplicate={attendee.resolvedEmail ? duplicateEmails.has(attendee.resolvedEmail.toLowerCase()) : false}
                        existingEmails={existingEmails}
                        onToggleFavorite={onToggleFavorite}
                        onAddProxy={onAddProxy}
                        hasProxy={principalsWithProxy.has(attendee.selectedEmployee?.login_name?.toLowerCase() ?? '')}
                        isReadOnly={isReadOnly}
                      />
                    ))}
                  </div>
                </>
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </ExpandableSection>

      {(showUploadModal || showUploadLocal) && (
        <UploadTemplateContainer
          visible={showUploadModal || showUploadLocal}
          onClose={() => {
            onHideUploadModal();
            setShowUploadLocal(false);
          }}
          isProcessingDocument={isProcessingDocument}
          documentError={documentError}
          onFileSelected={onFileSelected}
          onFormattedContentExtracted={onFormattedContentExtracted}
        />
      )}

      <ImportFromMeetingModal
        visible={showCopyFromMeeting}
        onDismiss={() => setShowCopyFromMeeting(false)}
        recentMeetings={recentMeetings}
        resolvedAttendees={resolvedAttendees}
        onImportComplete={(attendees) => {
          setShowCopyFromMeeting(false);
          if (attendees.length > 0) {
            loadAttendeesInBatches(attendees, onAddAttendee).catch((error) => {
              logger.error('Failed to load imported attendees', { error });
            });
          }
        }}
      />
    </>
  );
};
